import { concatHex, encodeAbiParameters, encodeFunctionData, encodePacked, getAddress, getCreate2Address, isHex, keccak256, } from "viem";
import { STRATEGY_FACTORY_ABI } from "../abis.js";
import { getDeployment } from "../addresses.js";
import { unavailable, usage } from "../errors.js";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const ZERO_VALUE = "0x0";
// ERC-1167 minimal-proxy creation code, split around the 20-byte implementation
// address. Byte-identical to OpenZeppelin `Clones.sol` (the same bytes
// `StrategyFactory.cloneAndInitDeterministic` deploys via CREATE2), so the
// CREATE2 address derived from it matches `Clones.predictDeterministicAddress`.
const ERC1167_PREFIX = "0x3d602d80600a3d3981f3363d3d373d3d3d363d73";
const ERC1167_SUFFIX = "0x5af43d82803e903d91602b57fd5bf3";
/**
 * Predict the CREATE2 address of an ERC-1167 clone of `template` deployed by
 * `factory` with `salt`. Mirrors OpenZeppelin `Clones.predictDeterministicAddress`.
 *
 * Pure — no RPC. The clone is deterministic in `(factory, template, salt)`
 * ONLY; it does NOT depend on `vault`/`proposer`. See `deriveStrategySalt` and
 * the note in `encodeStrategyDeploy` for the front-running implication.
 */
export function predictStrategyCloneAddress(factory, template, salt) {
    const initCode = concatHex([
        ERC1167_PREFIX,
        getAddress(template),
        ERC1167_SUFFIX,
    ]);
    return getCreate2Address({
        from: getAddress(factory),
        salt,
        bytecode: initCode,
    });
}
/**
 * Default CREATE2 salt for a strategy deploy: a keccak of the inputs. Two deploys
 * with identical `(vault, proposer, template, initData)` collide (the second
 * `cloneAndInitDeterministic` reverts, address already taken) — pass an explicit
 * `salt` to re-deploy the same parameters (e.g. propose → cancel → re-propose).
 */
export function deriveStrategySalt(vault, proposer, template, initData) {
    return keccak256(encodePacked(["address", "address", "address", "bytes"], [getAddress(vault), getAddress(proposer), getAddress(template), initData]));
}
/**
 * The actual CREATE2 salt the factory uses: `keccak256(abi.encode(vault, salt))`.
 *
 * `StrategyFactory.cloneAndInitDeterministic` folds the vault into the salt so
 * the predicted address lives in a per-vault namespace; combined with the
 * factory's `_authClone(vault)` gate (only the vault's owner/agents may deploy),
 * a front-runner who reads `salt` from the mempool cannot occupy the address.
 * This MUST stay byte-identical to the on-chain fold or predictions diverge from
 * the deployed address.
 */
export function effectiveStrategySalt(vault, salt) {
    return keccak256(encodeAbiParameters([{ type: "address" }, { type: "bytes32" }], [getAddress(vault), salt]));
}
function requireSalt(value) {
    if (typeof value !== "string" || !isHex(value) || value.length !== 66) {
        throw usage("`salt` must be a 0x-prefixed 32-byte hex string (66 chars)");
    }
    return value;
}
/**
 * Encode `StrategyFactory.cloneAndInitDeterministic(template, vault, proposer,
 * initData, salt)` — one unsigned tx that atomically clones the template via
 * CREATE2 and initializes it. Returns the predicted clone address so the caller
 * can build the proposal's `strategy` arg + executeCalls/settlementCalls against
 * it BEFORE the tx is mined — which is what unblocks keyless strategy proposals
 * (#387). Works for any template (Moonwell, Aerodrome, Venice, Portfolio, …):
 * the mechanism is template-agnostic, only `initData` differs.
 */
export function encodeStrategyDeploy(args, chainId) {
    const template = getAddress(args.template);
    const vault = getAddress(args.vault);
    const proposer = getAddress(args.proposer);
    if (typeof args.initData !== "string" || !isHex(args.initData)) {
        throw usage("`initData` must be a 0x-prefixed hex string");
    }
    if (template === ZERO_ADDRESS) {
        throw usage("`template` must be a non-zero address");
    }
    if (proposer === ZERO_ADDRESS) {
        throw usage("`proposer` must be a non-zero address");
    }
    const factory = args.factory
        ? getAddress(args.factory)
        : getDeployment(chainId).sherwood.strategyFactory;
    if (factory === ZERO_ADDRESS) {
        throw unavailable(`StrategyFactory is not yet deployed on chain ${chainId} — mirror its address into the SDK once it lands (issue #387), or pass an explicit \`factory\`.`);
    }
    // `salt` is what's passed in the calldata; the factory folds the vault in
    // (keccak256(abi.encode(vault, salt))) before CREATE2, so the prediction must
    // fold too — see `effectiveStrategySalt`.
    const salt = args.salt
        ? requireSalt(args.salt)
        : deriveStrategySalt(vault, proposer, template, args.initData);
    const clone = predictStrategyCloneAddress(factory, template, effectiveStrategySalt(vault, salt));
    const data = encodeFunctionData({
        abi: STRATEGY_FACTORY_ABI,
        functionName: "cloneAndInitDeterministic",
        args: [template, vault, proposer, args.initData, salt],
    });
    const tx = { to: factory, data, value: ZERO_VALUE, chainId };
    return {
        txs: [tx],
        preconditions: [],
        clone,
        salt,
        factory,
        description: `Deploy a strategy clone of ${template} for vault ${vault} (deterministic — clone at ${clone}).`,
        note: "Broadcast this tx from `proposer` — the factory requires proposer == msg.sender, and msg.sender must be the vault owner or a registered agent. The vault must be registered on the SyndicateFactory and `template` must be on the factory's allowlist (the factory reverts otherwise). The clone address is fixed by (factory, template, vault, salt) — reference it as the proposal `strategy` and inside executeCalls/settlementCalls. The factory binds the salt to the vault, and `_authClone` lets only the vault's owner/agents deploy there, so the predicted address can't be front-run.",
    };
}
